import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useOrder } from '../../context/OrderContext';
import { Header } from '../../layout';
import {
  ProfileSelector,
  CarterSelector,
  GlassSelector,
  FinishSelector,
  FixingSelector
} from '../../selectors';
import { ProcessingSection } from '../../features';

const STEPS = {
  PROFILE: 'profile',
  CARTER: 'carter',
  GLASS: 'glass',
  FINISH: 'finish',
  FIXING: 'fixing',
  PROCESSING: 'processing'
};

const OrderForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state: orderState, dispatch } = useOrder();
  const { currentStep, orderDetails } = orderState;
  const processingSectionRef = useRef(null);

  // Check if we're in edit mode and if an initial step was provided
  const isEditMode = location.state?.editMode;
  const initialStep = location.state?.initialStep;
  const category = location.state?.category;
  const subCategory = location.state?.subCategory;

  // Handle initial step if in edit mode
  useEffect(() => {
    if (isEditMode && initialStep) {
      // If in edit mode with an initial step, set the current step
      const stepKey = Object.values(STEPS)[initialStep - 1];
      if (stepKey) {
        dispatch({ type: 'SET_STEP', payload: stepKey });
      }
    }
    // Note: We no longer reset the order state here since it's handled in the Dashboard component
  }, [isEditMode, initialStep, dispatch]);

  // Check if we came from a specific product category
  useEffect(() => {
    if (category) {
      console.log(`Starting order process for category: ${category}, subcategory: ${subCategory || 'ordina'}`);

      // Set the category and subcategory in the order context
      dispatch({
        type: 'UPDATE_ORDER',
        payload: {
          category: category,
          subCategory: subCategory || 'ordina' // Use provided subcategory or default to 'ordina'
        }
      });
    }
  }, [category, subCategory, dispatch]);

  const handleProfileSelect = (profileData) => {
    dispatch({
      type: 'UPDATE_ORDER',
      payload: profileData
    });
    dispatch({ type: 'NEXT_STEP' });
  };

  const handleCarterSelect = (carterData) => {
    dispatch({
      type: 'UPDATE_ORDER',
      payload: carterData
    });
    dispatch({ type: 'NEXT_STEP' });
  };

  const handleGlassSelect = (glassData) => {
    dispatch({
      type: 'UPDATE_ORDER',
      payload: glassData
    });
    dispatch({ type: 'NEXT_STEP' });
  };

  const handleFinishSelect = (finishData) => {
    dispatch({
      type: 'UPDATE_ORDER',
      payload: finishData
    });
    dispatch({ type: 'NEXT_STEP' });
  };

  const handleFixingSelect = (fixingData) => {
    dispatch({
      type: 'UPDATE_ORDER',
      payload: fixingData
    });
    dispatch({ type: 'NEXT_STEP' });
  };

  const handleProcessingComplete = (completionData) => {
    if (completionData?.redirectToAccessories) {
      // Navigate to accessories page with profile context
      navigate('/accessories', {
        state: {
          category: completionData.profileContext.category,
          subCategory: completionData.profileContext.subCategory,
          profileType: orderDetails.profileType || orderDetails.ninfaNumber || 'N6',
          fromProfileConfiguration: true
        }
      });
    } else {
      // Default behavior - go to order summary (cart)
      navigate('/order-summary');
    }
  };

  const handleBack = () => {
    // If we're in the processing step, use the ref to go back within ProcessingSection
    if (currentStep === STEPS.PROCESSING && processingSectionRef.current) {
      // goBack returns false if ProcessingSection is at step 1 and can't go back further
      const handled = processingSectionRef.current.goBack();
      if (handled === false) {
        // ProcessingSection is at step 1, go to previous OrderForm step
        dispatch({ type: 'PREV_STEP' });
      }
    } else {
      dispatch({ type: 'PREV_STEP' });
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case STEPS.PROFILE:
        return <ProfileSelector onSelect={handleProfileSelect} />;
      case STEPS.CARTER:
        return <CarterSelector onSelect={handleCarterSelect} profileType={orderDetails.profileType} />;
      case STEPS.GLASS:
        return <GlassSelector
          onSelect={handleGlassSelect}
          selectedSize={orderDetails.glassSize}
          profileType={orderDetails.profileType}
        />;
      case STEPS.FINISH:
        return <FinishSelector
          onSelect={handleFinishSelect}
          selectedFinish={{
            finishY: orderDetails.finishY,
            finishC: orderDetails.finishC
          }}
          profileType={orderDetails.profileType}
        />;
      case STEPS.FIXING:
        return (
          <FixingSelector
            selectedFixing={orderDetails.fixing}
            onSelect={handleFixingSelect}
          />
        );
      case STEPS.PROCESSING:
        return (
          <ProcessingSection
            ref={processingSectionRef}
            onComplete={handleProcessingComplete}
            onBack={handleBack}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Header
        title={location.state?.type === 'offerta' ? 'Richiesta Offerta' : 'Nuovo Ordine'}
        backText="Categoria"
        backTo="/product-categories"
      />
      <div className="container mx-auto px-4 py-6">
        {currentStep !== STEPS.PROFILE && (
          <button
            onClick={handleBack}
            className="mb-4 px-4 py-2 text-gray-600 hover:text-gray-800 flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Indietro
          </button>
        )}
        {renderStep()}
        {orderDetails.productCode && (
          <div className="mt-6 p-4 bg-white rounded-lg shadow">
            <p className="text-center font-medium">Codice prodotto: {orderDetails.productCode}</p>
          </div>
        )}
      </div>

      {/* Cart Button */}
    </div>
  );
};

export default OrderForm;
